import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import styled from "styled-components";
import Loading from "../Loading/Loading";
import {
  EventsContainer,
  EventsHeader,
  EventsList,
} from "../Events/Events.styles";
import { AddFaq } from "./modals";
import { deleteFaq, getAllFaqs } from "../../utils/api-calls";
import { rel8Purple, rel8White } from "../../globals";
import { mobile } from "../../responsive";

// Styled Components
const FaqCard = styled.div`
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin-bottom: 20px;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
  }
`;

export const AddNewBtn = styled.button`
    border: none;
    color: ${rel8White};
    background-color: ${rel8Purple};
    padding: 10px 20px;
    border-radius: 10px;
    margin: 12px 0;
    cursor: pointer;
    ${
        mobile({
            margin: "20px 0px",
        })
    }
`;

const FaqQuestion = styled.h3`
  font-size: 18px;
  color: #333;
  margin-bottom: 12px;
  font-weight: 600;
`;

const FaqBody = styled.p`
  font-size: 16px;
  color: #555;
  line-height: 1.6;
`;

const FaqActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 15px;
`;

const ActionButton = styled.button`
  background-color: ${props => props.delete ? "#ff4d4f" : "#1890ff"};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.delete ? "#ff7875" : "#40a9ff"};
  }
  
  &:focus {
    outline: none;
  }
`;

// FaqTable Component
const FaqTable = ({ data }) => {
  const queryClient = useQueryClient();

  const handleDeleteClick = (faq) => {
    
    mutateDelete(faq.id);
  };

  const { mutate: mutateDelete } = useMutation(
    (id) => deleteFaq(id),
    {
      onSuccess: () => {
        toast.success("Faq deleted successfully");
        queryClient.invalidateQueries();
      },
      onError: () => {
        toast.error("Failed to delete Faq");
      }
    }
  )

  return (
    <>
      {data && data.length > 0 ? (
        data.map((faq) => (
          <FaqCard key={faq.id}>
            <FaqQuestion>{faq.name}</FaqQuestion>
            <FaqBody>{faq.body}</FaqBody>
            <FaqActions>
              <ActionButton 
                delete 
                onClick={() => handleDeleteClick(faq)}
              >
                Delete
              </ActionButton>
            </FaqActions>
          </FaqCard>
        ))
      ) : (
        <p>No FAQs found.</p>
      )}
    </>
  );
};

// Main FAQ Component
export const FAQ = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [showAddModal, setShowAddModal] = useState(false);
  const [faqs, setFaqs] = useState([]);

  const displayAddFaqModal = () => {
    setShowAddModal(!showAddModal);
  };

  const {
    isLoading: faqsLoading,
    isFetching: faqsFetching,
    isError: faqsError,
    // data: faqsData,
  } = useQuery("all-faqs", getAllFaqs, {
    refetchOnWindowFocus: false,
    select: (data) => data.data,
    onSuccess: (data) => {
      setFaqs(data.results);
    },
  });

  const handleAddFaq = (newFaq) => {
    setFaqs((prevFaqs) => [newFaq, ...prevFaqs]);
  };

  const handleDeleteFaq = (deletedId) => {
    setFaqs((prevFaqs) => prevFaqs.filter(faq => faq.id !== deletedId));
  };

  return (
    <>
      {showAddModal && (
        <AddFaq
          close={displayAddFaqModal} 
          onSubmit={handleAddFaq} 
        />
      )}
      
      <EventsContainer>
        <EventsHeader>Frequently Asked Questions</EventsHeader>
        <AddNewBtn onClick={displayAddFaqModal}>Add New FAQ</AddNewBtn>

        <EventsList>
          {faqsLoading || faqsFetching ? (
            <Loading loading={faqsLoading || faqsFetching} />
          ) : !faqsError ? (
            <FaqTable
              data={faqs}
              onDelete={handleDeleteFaq}
            />
          ) : (
            <small>Can't fetch FAQs</small>
          )}
        </EventsList>

      </EventsContainer>
    </>
  );
};

export default FAQ;